import { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  query,
  where,
} from 'firebase/firestore';
import { db, httpsCallable, functions } from '../config/firebase';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { increment } from 'firebase/firestore';
import {
  Search,
  User,
  Mail,
  Calendar,
  Eye,
  Edit,
  Trash2,
  Save,
  X,
  Bell,
  MessageSquare,
  Send,
  CheckSquare,
  Square,
  UserPlus,
  FileText,
} from 'lucide-react';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    username: '',
    photoURL: '',
    role: '',
    isAdmin: false,
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sendingNotification, setSendingNotification] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(null);
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkSendingNotification, setBulkSendingNotification] = useState(false);
  const [bulkSendingEmail, setBulkSendingEmail] = useState(false);
  const [bulkSendingMessage, setBulkSendingMessage] = useState(false);
  const [isBulkSendingNotification, setIsBulkSendingNotification] =
    useState(false);
  const [isBulkSendingEmail, setIsBulkSendingEmail] = useState(false);
  const [isBulkSendingMessage, setIsBulkSendingMessage] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    body: '',
  });
  const [emailForm, setEmailForm] = useState({
    subject: '',
    message: '',
  });
  const [messageForm, setMessageForm] = useState({
    text: '',
  });
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    username: '',
    phoneNumber: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      // Fetch all users without orderBy to ensure we get all users
      // Some users might not have createdAt field
      const usersSnapshot = await getDocs(collection(db, 'users'));

      const allUsers = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort client-side by createdAt (newest first)
      // Users without createdAt will appear at the end
      allUsers.sort((a, b) => {
        const aDate = a.createdAt?.toDate
          ? a.createdAt.toDate().getTime()
          : a.createdAt
          ? new Date(a.createdAt).getTime()
          : 0;
        const bDate = b.createdAt?.toDate
          ? b.createdAt.toDate().getTime()
          : b.createdAt
          ? new Date(b.createdAt).getTime()
          : 0;

        // If both have dates, sort descending (newest first)
        // If one doesn't have date, put it at the end
        if (aDate === 0 && bDate === 0) return 0;
        if (aDate === 0) return 1;
        if (bDate === 0) return -1;
        return bDate - aDate;
      });

      setUsers(allUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower) ||
      user.username?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleEdit = user => {
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      username: user.username || '',
      photoURL: user.photoURL || '',
      role: user.role || 'user',
      isAdmin: user.isAdmin || user.role === 'admin' || false,
    });
  };

  const handleSave = async () => {
    if (!editingUser) return;

    try {
      const userRef = doc(db, 'users', editingUser.id);
      const updateData = {
        firstName: editForm.firstName.trim() || null,
        lastName: editForm.lastName.trim() || null,
        email: editForm.email.trim(),
        phoneNumber: editForm.phoneNumber.trim() || null,
        username: editForm.username.trim() || null,
        photoURL: editForm.photoURL.trim() || null,
      };

      // Update role/admin status
      if (editForm.isAdmin || editForm.role === 'admin') {
        updateData.role = 'admin';
        updateData.isAdmin = true;
      } else {
        updateData.role = editForm.role || 'user';
        updateData.isAdmin = false;
      }

      await updateDoc(userRef, updateData);

      // Update local state
      setUsers(
        users.map(u => (u.id === editingUser.id ? { ...u, ...updateData } : u)),
      );

      setEditingUser(null);
      setEditForm({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        username: '',
        photoURL: '',
        role: '',
        isAdmin: false,
      });
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user: ' + error.message);
    }
  };

  const handleDelete = async userId => {
    try {
      await deleteDoc(doc(db, 'users', userId));
      setUsers(users.filter(u => u.id !== userId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user: ' + error.message);
    }
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditForm({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      username: '',
      photoURL: '',
      role: '',
      isAdmin: false,
    });
  };

  const handleSendNotification = async userId => {
    if (isSendingNotification) return; // Prevent multiple clicks

    if (!notificationForm.title || !notificationForm.body) {
      alert('Please fill in both title and body');
      return;
    }

    setIsSendingNotification(true);
    try {
      const sendNotification = httpsCallable(functions, 'sendNotification');
      const result = await sendNotification({
        userId,
        title: notificationForm.title,
        body: notificationForm.body,
        data: { type: 'admin_message' },
      });

      if (result.data.success) {
        alert('Notification sent successfully!');
        setSendingNotification(null);
        setNotificationForm({ title: '', body: '' });
      } else {
        throw new Error('Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Failed to send notification: ' + error.message);
    } finally {
      setIsSendingNotification(false);
    }
  };

  const handleSendEmail = async user => {
    if (isSendingEmail) return; // Prevent multiple clicks

    if (!emailForm.subject || !emailForm.message) {
      alert('Please fill in both subject and message');
      return;
    }

    if (!user.email) {
      alert('User does not have an email address');
      return;
    }

    setIsSendingEmail(true);
    try {
      // Use callable function for sending email (more reliable than HTTP)
      const sendEmail = httpsCallable(functions, 'sendEmail');
      const result = await sendEmail({
        userEmail: user.email,
        title: emailForm.subject,
        body: emailForm.message,
        type: 'admin_message',
      });

      if (result.data && result.data.success) {
        alert('Email sent successfully!');
        setSendingEmail(null);
        setEmailForm({ subject: '', message: '' });
      } else {
        const errorMsg = result.data?.error || 'Failed to send email';
        alert(errorMsg);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      let errorMessage = 'Failed to send email';

      // Handle Firebase callable function errors
      if (error.code === 'functions/internal') {
        errorMessage = 'Internal server error. Please try again later.';
      } else if (error.code === 'functions/unauthenticated') {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (error.code === 'functions/permission-denied') {
        errorMessage =
          'Permission denied. You do not have access to this function.';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.code) {
        errorMessage = `Error ${error.code}: ${
          error.message || 'Unknown error'
        }`;
      }

      alert(errorMessage);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSendMessage = async user => {
    if (isSendingMessage) return; // Prevent multiple clicks

    if (!messageForm.text.trim()) {
      alert('Please enter a message');
      return;
    }

    // Validate user object
    if (!user || !user.id) {
      alert('Invalid user selected');
      return;
    }

    const targetUserId = user.id; // Store target user ID to ensure we use the correct one
    console.log('Sending message to user:', targetUserId, user.email);

    setIsSendingMessage(true);
    try {
      // Get current admin user
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Admin user not authenticated');
      }

      // Get admin user document from Firestore to ensure we have correct info
      const adminUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const adminUserData = adminUserDoc.exists() ? adminUserDoc.data() : null;

      // Determine admin display name
      const adminDisplayName =
        adminUserData?.role === 'admin' || adminUserData?.isAdmin
          ? 'Admin'
          : 'Admin'; // Always use "Admin" for admin panel messages

      console.log('Admin user ID:', currentUser.uid);
      console.log('Admin display name:', adminDisplayName);
      console.log('Target user ID:', targetUserId);

      // Find or create conversation between admin and target user
      // Use where query to optimize search
      const conversationsRef = collection(db, 'conversations');
      const conversationsQuery = query(
        conversationsRef,
        where('participants', 'array-contains', currentUser.uid),
      );
      const conversationsSnapshot = await getDocs(conversationsQuery);

      let conversationId = null;
      conversationsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const participants = data.participants || [];
        // Check if this conversation is between admin and target user only
        if (
          Array.isArray(participants) &&
          participants.length === 2 &&
          participants.includes(currentUser.uid) &&
          participants.includes(targetUserId)
        ) {
          conversationId = doc.id;
          console.log('Found existing conversation:', conversationId);
        }
      });

      // Get target user info for conversation name
      const targetUserDoc = await getDoc(doc(db, 'users', targetUserId));
      const targetUserData = targetUserDoc.exists()
        ? targetUserDoc.data()
        : null;
      const targetUserName =
        targetUserData?.displayName ||
        targetUserData?.firstName ||
        targetUserData?.email ||
        'User';

      // Create conversation if it doesn't exist
      if (!conversationId) {
        console.log(
          'Creating new conversation between',
          currentUser.uid,
          'and',
          targetUserId,
        );
        const newConversation = await addDoc(conversationsRef, {
          participants: [currentUser.uid, targetUserId],
          participantName: targetUserName, // Use target user's name, not "Admin"
          conversationName: targetUserName, // Use target user's name
          adminParticipant: true, // Flag to indicate admin is a participant
          createdAt: serverTimestamp(),
          lastMessage: messageForm.text.trim(),
          lastMessageTime: serverTimestamp(),
          unreadCount: {
            [currentUser.uid]: 0,
            [targetUserId]: 1,
          },
        });
        conversationId = newConversation.id;
        console.log('Created new conversation:', conversationId);
      } else {
        // Update conversation - don't override participantName, keep it as the other user's name
        console.log('Updating existing conversation:', conversationId);
        await updateDoc(doc(db, 'conversations', conversationId), {
          // Don't override participantName - it should show the other user's name
          adminParticipant: true, // Flag to indicate admin is a participant
          lastMessage: messageForm.text.trim(),
          lastMessageTime: serverTimestamp(),
          [`unreadCount.${currentUser.uid}`]: 0,
          [`unreadCount.${targetUserId}`]: increment(1),
        });
      }

      // Add message with explicit admin information
      console.log('Adding message to conversation:', conversationId);
      const messagesRef = collection(db, 'messages');
      await addDoc(messagesRef, {
        conversationId,
        text: messageForm.text.trim(),
        senderId: currentUser.uid,
        senderName: adminDisplayName, // Use determined admin name
        senderDisplayName: 'Admin', // Explicit admin display name
        senderEmail: currentUser.email || 'admin@flexyswap.com',
        isAdmin: true,
        role: 'admin',
        adminMessage: true, // Additional flag for admin messages
        createdAt: serverTimestamp(),
        isRead: false,
      });

      // Also update admin user's displayName in Firestore if needed to ensure consistency
      if (
        adminUserData &&
        (adminUserData.displayName !== 'Admin' || !adminUserData.isAdmin)
      ) {
        try {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            displayName: 'Admin',
            isAdmin: true,
            role: 'admin',
          });
        } catch (updateError) {
          console.warn('Failed to update admin user displayName:', updateError);
        }
      }

      // Send notification to target user
      try {
        console.log('Sending notification to user:', targetUserId);
        const sendNotification = httpsCallable(functions, 'sendNotification');
        await sendNotification({
          userId: targetUserId,
          title: 'New Message from Admin',
          body: messageForm.text.trim().substring(0, 100),
          data: { type: 'admin_message', conversationId },
        });
      } catch (notifError) {
        console.warn('Failed to send notification:', notifError);
      }

      alert('Message sent successfully!');
      setSendingMessage(null);
      setMessageForm({ text: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + error.message);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleBulkSendNotification = async () => {
    if (isBulkSendingNotification) return;
    if (selectedUsers.length === 0) {
      alert('Please select at least one user');
      return;
    }
    if (!notificationForm.title || !notificationForm.body) {
      alert('Please fill in both title and body');
      return;
    }

    setIsBulkSendingNotification(true);
    try {
      const sendNotificationToMultiple = httpsCallable(
        functions,
        'sendNotificationToMultiple',
      );
      const result = await sendNotificationToMultiple({
        userIds: selectedUsers,
        title: notificationForm.title,
        body: notificationForm.body,
        data: { type: 'admin_message' },
      });

      if (result.data.success) {
        const successCount = result.data.results.filter(r => r.success).length;
        alert(
          `Notifications sent successfully to ${successCount} out of ${selectedUsers.length} users!`,
        );
        setBulkSendingNotification(false);
        setNotificationForm({ title: '', body: '' });
        setSelectedUsers([]);
      } else {
        throw new Error('Failed to send notifications');
      }
    } catch (error) {
      console.error('Error sending bulk notifications:', error);
      alert('Failed to send notifications: ' + error.message);
    } finally {
      setIsBulkSendingNotification(false);
    }
  };

  const handleBulkSendEmail = async () => {
    if (isBulkSendingEmail) return;
    if (selectedUsers.length === 0) {
      alert('Please select at least one user');
      return;
    }
    if (!emailForm.subject || !emailForm.message) {
      alert('Please fill in both subject and message');
      return;
    }

    setIsBulkSendingEmail(true);
    try {
      // Get selected users with emails
      const usersWithEmails = filteredUsers.filter(
        u => selectedUsers.includes(u.id) && u.email,
      );

      if (usersWithEmails.length === 0) {
        alert('Selected users do not have email addresses');
        setIsBulkSendingEmail(false);
        return;
      }

      let successCount = 0;
      let failCount = 0;

      // Send emails one by one
      for (const user of usersWithEmails) {
        try {
          const sendEmail = httpsCallable(functions, 'sendEmail');
          const result = await sendEmail({
            userEmail: user.email,
            title: emailForm.subject,
            body: emailForm.message,
            type: 'admin_message',
          });

          if (result.data.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`Error sending email to ${user.email}:`, error);
          failCount++;
        }
      }

      alert(
        `Emails sent: ${successCount} successful, ${failCount} failed out of ${usersWithEmails.length} users`,
      );
      setBulkSendingEmail(false);
      setEmailForm({ subject: '', message: '' });
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error sending bulk emails:', error);
      alert('Failed to send emails: ' + error.message);
    } finally {
      setIsBulkSendingEmail(false);
    }
  };

  const handleBulkSendMessage = async () => {
    if (isBulkSendingMessage) return;
    if (selectedUsers.length === 0) {
      alert('Please select at least one user');
      return;
    }
    if (!messageForm.text.trim()) {
      alert('Please enter a message');
      return;
    }

    setIsBulkSendingMessage(true);
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Admin user not authenticated');
      }

      // Get admin user document from Firestore
      const adminUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const adminUserData = adminUserDoc.exists() ? adminUserDoc.data() : null;

      // Determine admin display name
      const adminDisplayName = 'Admin'; // Always use "Admin" for admin panel messages

      const selectedUsersList = filteredUsers.filter(u =>
        selectedUsers.includes(u.id),
      );

      let successCount = 0;
      let failCount = 0;

      for (const user of selectedUsersList) {
        try {
          const targetUserId = user.id; // Store target user ID
          console.log('Bulk sending message to user:', targetUserId);

          // Get target user name
          let targetUserName =
            user.displayName || user.firstName || user.email || 'User';

          // Find or create conversation using optimized query
          const conversationsRef = collection(db, 'conversations');
          const conversationsQuery = query(
            conversationsRef,
            where('participants', 'array-contains', currentUser.uid),
          );
          const conversationsSnapshot = await getDocs(conversationsQuery);

          let conversationId = null;
          conversationsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const participants = data.participants || [];
            // Check if this conversation is between admin and target user only
            if (
              Array.isArray(participants) &&
              participants.length === 2 &&
              participants.includes(currentUser.uid) &&
              participants.includes(targetUserId)
            ) {
              conversationId = doc.id;
            }
          });

          if (!conversationId) {
            const newConversation = await addDoc(conversationsRef, {
              participants: [currentUser.uid, targetUserId],
              participantName: targetUserName, // Use target user's name, not "Admin"
              conversationName: targetUserName, // Use target user's name
              adminParticipant: true, // Flag to indicate admin is a participant
              createdAt: serverTimestamp(),
              lastMessage: messageForm.text.trim(),
              lastMessageTime: serverTimestamp(),
              unreadCount: {
                [currentUser.uid]: 0,
                [targetUserId]: 1,
              },
            });
            conversationId = newConversation.id;
          } else {
            await updateDoc(doc(db, 'conversations', conversationId), {
              // Don't override participantName - it should show the other user's name
              adminParticipant: true, // Flag to indicate admin is a participant
              lastMessage: messageForm.text.trim(),
              lastMessageTime: serverTimestamp(),
              [`unreadCount.${currentUser.uid}`]: 0,
              [`unreadCount.${targetUserId}`]: increment(1),
            });
          }

          // Add message with explicit admin information
          const messagesRef = collection(db, 'messages');
          await addDoc(messagesRef, {
            conversationId,
            text: messageForm.text.trim(),
            senderId: currentUser.uid,
            senderName: adminDisplayName, // Use determined admin name
            senderDisplayName: 'Admin', // Explicit admin display name
            senderEmail: currentUser.email || 'admin@flexyswap.com',
            isAdmin: true,
            role: 'admin',
            adminMessage: true, // Additional flag for admin messages
            createdAt: serverTimestamp(),
            isRead: false,
          });

          // Send notification to target user
          try {
            const sendNotification = httpsCallable(
              functions,
              'sendNotification',
            );
            await sendNotification({
              userId: targetUserId,
              title: 'New Message from Admin',
              body: messageForm.text.trim().substring(0, 100),
              data: { type: 'admin_message', conversationId },
            });
          } catch (notifError) {
            console.warn(
              `Failed to send notification to ${targetUserId}:`,
              notifError,
            );
          }

          successCount++;
        } catch (error) {
          console.error(`Error sending message to ${user.id}:`, error);
          failCount++;
        }
      }

      alert(
        `Messages sent: ${successCount} successful, ${failCount} failed out of ${selectedUsersList.length} users`,
      );
      setBulkSendingMessage(false);
      setMessageForm({ text: '' });
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error sending bulk messages:', error);
      alert('Failed to send messages: ' + error.message);
    } finally {
      setIsBulkSendingMessage(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (creatingAdmin) return;

    // Validation
    if (!newAdminForm.email || !newAdminForm.email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    if (!newAdminForm.password || newAdminForm.password.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    if (newAdminForm.password !== newAdminForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (!newAdminForm.firstName || !newAdminForm.lastName) {
      alert('Please enter first name and last name');
      return;
    }

    setCreatingAdmin(true);
    try {
      const auth = getAuth();

      // Create user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newAdminForm.email,
        newAdminForm.password,
      );

      const newUserId = userCredential.user.uid;

      // Create user document in Firestore with admin privileges
      const usernameLower = (
        newAdminForm.username || newAdminForm.email.split('@')[0]
      )
        .toLowerCase()
        .trim();

      await setDoc(doc(db, 'users', newUserId), {
        uid: newUserId,
        email: newAdminForm.email,
        firstName: newAdminForm.firstName.trim(),
        lastName: newAdminForm.lastName.trim(),
        displayName: `${newAdminForm.firstName.trim()} ${newAdminForm.lastName.trim()}`,
        username: usernameLower,
        phoneNumber: newAdminForm.phoneNumber || '',
        role: 'admin',
        isAdmin: true,
        createdAt: serverTimestamp(),
        emailVerified: false,
      });

      // Also create username document if username is provided
      if (newAdminForm.username) {
        try {
          await setDoc(doc(db, 'usernames', usernameLower), {
            userId: newUserId,
            uid: newUserId,
            email: newAdminForm.email,
            username: usernameLower,
            createdAt: serverTimestamp(),
          });
        } catch (usernameError) {
          console.warn('Error creating username document:', usernameError);
          // Non-fatal, continue
        }
      }

      alert('Admin user created successfully!');
      setShowCreateAdmin(false);
      setNewAdminForm({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        username: '',
        phoneNumber: '',
      });

      // Refresh users list
      await fetchUsers();
    } catch (error) {
      console.error('Error creating admin user:', error);
      let errorMessage = 'Failed to create admin user';

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    } finally {
      setCreatingAdmin(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black">Users</h1>
          <p className="text-gray-700 mt-1">
            Total {users.length} users
            {selectedUsers.length > 0 && (
              <span className="ml-2 text-yellow-600 font-semibold">
                ({selectedUsers.length} selected)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowCreateAdmin(true)}
            className="bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-yellow-400 font-semibold flex items-center"
          >
            <UserPlus size={18} className="mr-2" />
            Create Admin
          </button>
        </div>
        {selectedUsers.length > 0 && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setBulkSendingNotification(true);
                setNotificationForm({ title: '', body: '' });
              }}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 font-semibold flex items-center"
            >
              <Bell size={18} className="mr-2" />
              Send Notification ({selectedUsers.length})
            </button>
            <button
              onClick={() => {
                setBulkSendingEmail(true);
                setEmailForm({ subject: '', message: '' });
              }}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-semibold flex items-center"
            >
              <Mail size={18} className="mr-2" />
              Send Email ({selectedUsers.length})
            </button>
            <button
              onClick={() => {
                setBulkSendingMessage(true);
                setMessageForm({ text: '' });
              }}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 font-semibold flex items-center"
            >
              <MessageSquare size={18} className="mr-2" />
              Send Message ({selectedUsers.length})
            </button>
            <button
              onClick={() => setSelectedUsers([])}
              className="bg-gray-300 text-black px-4 py-2 rounded-lg hover:bg-gray-400 font-semibold"
            >
              Clear Selection
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          size={20}
        />
        <input
          type="text"
          placeholder="Search users (email, name, username)..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none bg-white"
        />
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-black overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider w-12">
                  <button
                    onClick={() => {
                      if (selectedUsers.length === filteredUsers.length) {
                        setSelectedUsers([]);
                      } else {
                        setSelectedUsers(filteredUsers.map(u => u.id));
                      }
                    }}
                    className="flex items-center"
                  >
                    {selectedUsers.length === filteredUsers.length &&
                    filteredUsers.length > 0 ? (
                      <CheckSquare size={20} className="text-yellow-400" />
                    ) : (
                      <Square size={20} className="text-yellow-400" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider">
                  Registration Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-yellow-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {searchTerm ? 'No users found' : 'No users yet'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map(user => (
                  <tr
                    key={user.id}
                    className={`hover:bg-yellow-50 ${
                      selectedUsers.includes(user.id) ? 'bg-yellow-100' : ''
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          if (selectedUsers.includes(user.id)) {
                            setSelectedUsers(
                              selectedUsers.filter(id => id !== user.id),
                            );
                          } else {
                            setSelectedUsers([...selectedUsers, user.id]);
                          }
                        }}
                        className="flex items-center"
                      >
                        {selectedUsers.includes(user.id) ? (
                          <CheckSquare size={20} className="text-yellow-600" />
                        ) : (
                          <Square size={20} className="text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {user.photoURL ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover border-2 border-yellow-500"
                              src={user.photoURL}
                              alt={user.firstName || user.email}
                              onError={e => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={`h-10 w-10 rounded-full bg-yellow-500 flex items-center justify-center border-2 border-black ${
                              user.photoURL ? 'hidden' : ''
                            }`}
                          >
                            {user.firstName || user.lastName ? (
                              <span className="text-black font-bold text-sm">
                                {(
                                  (user.firstName?.[0] || '') +
                                  (user.lastName?.[0] || '')
                                )
                                  .toUpperCase()
                                  .slice(0, 2) || 'U'}
                              </span>
                            ) : (
                              <User className="text-black" size={20} />
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName && user.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user.username || user.email}
                          </div>
                          {user.username && (
                            <div className="text-sm text-gray-500">
                              @{user.username}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Mail size={16} className="mr-2 text-gray-400" />
                        {user.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar size={16} className="mr-2 text-gray-400" />
                        {formatDate(user.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.role === 'admin' || user.isAdmin
                            ? 'bg-yellow-500 text-black'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {user.role === 'admin' || user.isAdmin
                          ? 'Admin'
                          : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2 flex-wrap gap-1">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="text-yellow-600 hover:text-yellow-800 flex items-center"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-blue-600 hover:text-blue-800 flex items-center"
                          title="Edit User"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSendingNotification(user.id);
                            setNotificationForm({ title: '', body: '' });
                          }}
                          className="text-purple-600 hover:text-purple-800 flex items-center"
                          title="Send Notification"
                        >
                          <Bell size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSendingEmail(user);
                            setEmailForm({ subject: '', message: '' });
                          }}
                          className="text-green-600 hover:text-green-800 flex items-center"
                          title="Send Email"
                        >
                          <Mail size={16} />
                        </button>
                        <button
                          onClick={() => {
                            // Create a copy of user object to prevent reference issues
                            setSendingMessage({ ...user });
                            setMessageForm({ text: '' });
                          }}
                          className="text-indigo-600 hover:text-indigo-800 flex items-center"
                          title="Send Message"
                        >
                          <MessageSquare size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(user.id)}
                          className="text-red-600 hover:text-red-800 flex items-center"
                          title="Delete User"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border-2 border-black">
            <h3 className="text-xl font-bold text-black mb-4">
              Confirm Delete
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this user? This action cannot be
              undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 font-semibold"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 bg-gray-300 text-black py-2 px-4 rounded-lg hover:bg-gray-400 font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-black">
            <div className="p-6 border-b-2 border-black bg-black">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-yellow-400">
                  Edit User
                </h2>
                <button
                  onClick={cancelEdit}
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Avatar Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Avatar
                </label>
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    {editForm.photoURL ? (
                      <img
                        className="h-20 w-20 rounded-full object-cover border-2 border-yellow-500"
                        src={editForm.photoURL}
                        alt="Avatar preview"
                        onError={e => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-yellow-500 flex items-center justify-center border-2 border-black">
                        <User className="text-black" size={40} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="url"
                      value={editForm.photoURL}
                      onChange={e =>
                        setEditForm({ ...editForm, photoURL: e.target.value })
                      }
                      placeholder="Avatar URL (e.g., https://example.com/avatar.jpg)"
                      className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter a URL for the user's profile picture
                    </p>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={editForm.firstName}
                    onChange={e =>
                      setEditForm({ ...editForm, firstName: e.target.value })
                    }
                    className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                    placeholder="First Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={editForm.lastName}
                    onChange={e =>
                      setEditForm({ ...editForm, lastName: e.target.value })
                    }
                    className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                    placeholder="Last Name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={e =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                  required
                  className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  placeholder="user@example.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    value={editForm.username}
                    onChange={e =>
                      setEditForm({ ...editForm, username: e.target.value })
                    }
                    className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                    placeholder="username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={editForm.phoneNumber}
                    onChange={e =>
                      setEditForm({ ...editForm, phoneNumber: e.target.value })
                    }
                    className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                    placeholder="+1234567890"
                  />
                </div>
              </div>

              {/* Role & Admin Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={editForm.role}
                    onChange={e => {
                      const role = e.target.value;
                      setEditForm({
                        ...editForm,
                        role,
                        isAdmin: role === 'admin',
                      });
                    }}
                    className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Status
                  </label>
                  <div className="flex items-center h-10">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.isAdmin}
                        onChange={e =>
                          setEditForm({
                            ...editForm,
                            isAdmin: e.target.checked,
                            role: e.target.checked ? 'admin' : 'user',
                          })
                        }
                        className="w-5 h-5 text-yellow-500 border-2 border-black rounded focus:ring-yellow-500"
                      />
                      <span className="text-gray-700">
                        {editForm.isAdmin ? 'Admin User' : 'Regular User'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4 border-t-2 border-gray-200">
                <button
                  onClick={handleSave}
                  className="flex-1 bg-black text-yellow-400 py-3 px-4 rounded-lg hover:bg-yellow-500 hover:text-black font-semibold transition"
                >
                  <Save size={20} className="inline mr-2" />
                  Save Changes
                </button>
                <button
                  onClick={cancelEdit}
                  className="flex-1 bg-gray-300 text-black py-3 px-4 rounded-lg hover:bg-gray-400 font-semibold transition"
                >
                  <X size={20} className="inline mr-2" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-black">
            <div className="p-6 border-b-2 border-black bg-black">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-yellow-400">
                  User Details
                </h2>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center space-x-4">
                {selectedUser.photoURL ? (
                  <img
                    className="h-20 w-20 rounded-full object-cover border-2 border-yellow-500"
                    src={selectedUser.photoURL}
                    alt={selectedUser.firstName || selectedUser.email}
                    onError={e => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className={`h-20 w-20 rounded-full bg-yellow-500 flex items-center justify-center border-2 border-black ${
                    selectedUser.photoURL ? 'hidden' : ''
                  }`}
                >
                  {selectedUser.firstName || selectedUser.lastName ? (
                    <span className="text-black font-bold text-lg">
                      {(
                        (selectedUser.firstName?.[0] || '') +
                        (selectedUser.lastName?.[0] || '')
                      )
                        .toUpperCase()
                        .slice(0, 2) || 'U'}
                    </span>
                  ) : (
                    <User className="text-black" size={40} />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {selectedUser.firstName && selectedUser.lastName
                      ? `${selectedUser.firstName} ${selectedUser.lastName}`
                      : selectedUser.username || selectedUser.email}
                  </h3>
                  {selectedUser.username && (
                    <p className="text-gray-600">@{selectedUser.username}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Email
                  </label>
                  <p className="text-gray-900">{selectedUser.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Phone
                  </label>
                  <p className="text-gray-900">
                    {selectedUser.phoneNumber || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Registration Date
                  </label>
                  <p className="text-gray-900">
                    {formatDate(selectedUser.createdAt)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Wallet Balance
                  </label>
                  <p className="text-gray-900">
                    ${selectedUser.walletBalance?.toFixed(2) || '0.00'}
                  </p>
                </div>
                {selectedUser.averageRating && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Average Rating
                    </label>
                    <p className="text-gray-900">
                      {selectedUser.averageRating.toFixed(1)} / 5.0
                    </p>
                  </div>
                )}
                {selectedUser.totalReviews && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Total Reviews
                    </label>
                    <p className="text-gray-900">{selectedUser.totalReviews}</p>
                  </div>
                )}
              </div>

              {/* Verification Section */}
              {selectedUser.verificationFileUrl && (
                <div className="border-t-2 border-gray-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Identity Verification
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Document Type
                      </label>
                      <p className="text-gray-900 capitalize">
                        {selectedUser.verificationFileType || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Status
                      </label>
                      <p className="text-gray-900">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            selectedUser.verificationStatus === 'verified'
                              ? 'bg-green-100 text-green-800'
                              : selectedUser.verificationStatus === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {selectedUser.verificationStatus || 'Not Submitted'}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 mb-2 block">
                        Verification Document
                      </label>
                      <div className="flex items-center space-x-4">
                        <a
                          href={selectedUser.verificationFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline flex items-center"
                        >
                          <FileText size={20} className="mr-2" />
                          {selectedUser.verificationFileName || 'View Document'}
                        </a>
                      </div>
                    </div>
                    {selectedUser.verificationStatus === 'pending' && (
                      <div className="flex space-x-3 pt-4">
                        <button
                          onClick={async () => {
                            if (
                              !confirm(
                                'Are you sure you want to approve this verification?',
                              )
                            ) {
                              return;
                            }
                            try {
                              await updateDoc(
                                doc(db, 'users', selectedUser.id),
                                {
                                  verificationStatus: 'verified',
                                  verificationApprovedAt: serverTimestamp(),
                                },
                              );
                              alert('Verification approved successfully');
                              setSelectedUser({
                                ...selectedUser,
                                verificationStatus: 'verified',
                              });
                              fetchUsers();
                            } catch (error) {
                              console.error(
                                'Error approving verification:',
                                error,
                              );
                              alert('Failed to approve verification');
                            }
                          }}
                          className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 font-semibold"
                        >
                          Approve
                        </button>
                        <button
                          onClick={async () => {
                            if (
                              !confirm(
                                'Are you sure you want to reject this verification?',
                              )
                            ) {
                              return;
                            }
                            try {
                              await updateDoc(
                                doc(db, 'users', selectedUser.id),
                                {
                                  verificationStatus: 'rejected',
                                  verificationRejectedAt: serverTimestamp(),
                                },
                              );
                              alert('Verification rejected');
                              setSelectedUser({
                                ...selectedUser,
                                verificationStatus: 'rejected',
                              });
                              fetchUsers();
                            } catch (error) {
                              console.error(
                                'Error rejecting verification:',
                                error,
                              );
                              alert('Failed to reject verification');
                            }
                          }}
                          className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 font-semibold"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Send Notification Modal */}
      {sendingNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full border-2 border-black">
            <div className="p-6 border-b-2 border-black bg-black">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-yellow-400">
                  Send Notification
                </h2>
                <button
                  onClick={() => {
                    setSendingNotification(null);
                    setNotificationForm({ title: '', body: '' });
                  }}
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={notificationForm.title}
                  onChange={e =>
                    setNotificationForm({
                      ...notificationForm,
                      title: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  placeholder="Notification title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={notificationForm.body}
                  onChange={e =>
                    setNotificationForm({
                      ...notificationForm,
                      body: e.target.value,
                    })
                  }
                  rows={4}
                  className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  placeholder="Notification message"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => handleSendNotification(sendingNotification)}
                  disabled={
                    !notificationForm.title ||
                    !notificationForm.body ||
                    isSendingNotification
                  }
                  className="flex-1 bg-black text-yellow-400 py-3 px-4 rounded-lg hover:bg-yellow-500 hover:text-black font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSendingNotification ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-400 mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} className="inline mr-2" />
                      Send Notification
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setSendingNotification(null);
                    setNotificationForm({ title: '', body: '' });
                  }}
                  disabled={isSendingNotification}
                  className="flex-1 bg-gray-300 text-black py-3 px-4 rounded-lg hover:bg-gray-400 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {sendingEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full border-2 border-black">
            <div className="p-6 border-b-2 border-black bg-black">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-yellow-400">
                  Send Email
                </h2>
                <button
                  onClick={() => {
                    setSendingEmail(null);
                    setEmailForm({ subject: '', message: '' });
                  }}
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To: {sendingEmail.email}
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={e =>
                    setEmailForm({ ...emailForm, subject: e.target.value })
                  }
                  className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  placeholder="Email subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={emailForm.message}
                  onChange={e =>
                    setEmailForm({ ...emailForm, message: e.target.value })
                  }
                  rows={6}
                  className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  placeholder="Email message"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => handleSendEmail(sendingEmail)}
                  disabled={
                    !emailForm.subject || !emailForm.message || isSendingEmail
                  }
                  className="flex-1 bg-black text-yellow-400 py-3 px-4 rounded-lg hover:bg-yellow-500 hover:text-black font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSendingEmail ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-400 mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} className="inline mr-2" />
                      Send Email
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setSendingEmail(null);
                    setEmailForm({ subject: '', message: '' });
                  }}
                  disabled={isSendingEmail}
                  className="flex-1 bg-gray-300 text-black py-3 px-4 rounded-lg hover:bg-gray-400 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Message Modal */}
      {sendingMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full border-2 border-black">
            <div className="p-6 border-b-2 border-black bg-black">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-yellow-400">
                  Send Message
                </h2>
                <button
                  onClick={() => {
                    setSendingMessage(null);
                    setMessageForm({ text: '' });
                  }}
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To:{' '}
                  {sendingMessage?.firstName ||
                    sendingMessage?.username ||
                    sendingMessage?.email ||
                    'Unknown User'}
                  {sendingMessage?.id && (
                    <span className="text-xs text-gray-500 ml-2">
                      (ID: {sendingMessage.id.substring(0, 8)}...)
                    </span>
                  )}
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={messageForm.text}
                  onChange={e =>
                    setMessageForm({ ...messageForm, text: e.target.value })
                  }
                  rows={6}
                  className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  placeholder="Type your message here..."
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    if (!sendingMessage || !sendingMessage.id) {
                      alert('Invalid user selected. Please try again.');
                      return;
                    }
                    handleSendMessage(sendingMessage);
                  }}
                  disabled={
                    !messageForm.text.trim() ||
                    isSendingMessage ||
                    !sendingMessage?.id
                  }
                  className="flex-1 bg-black text-yellow-400 py-3 px-4 rounded-lg hover:bg-yellow-500 hover:text-black font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSendingMessage ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-400 mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} className="inline mr-2" />
                      Send Message
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setSendingMessage(null);
                    setMessageForm({ text: '' });
                  }}
                  disabled={isSendingMessage}
                  className="flex-1 bg-gray-300 text-black py-3 px-4 rounded-lg hover:bg-gray-400 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Send Notification Modal */}
      {bulkSendingNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full border-2 border-black">
            <div className="p-6 border-b-2 border-black bg-black">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-yellow-400">
                  Send Notification to {selectedUsers.length} Users
                </h2>
                <button
                  onClick={() => {
                    setBulkSendingNotification(false);
                    setNotificationForm({ title: '', body: '' });
                  }}
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={notificationForm.title}
                  onChange={e =>
                    setNotificationForm({
                      ...notificationForm,
                      title: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  placeholder="Notification title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={notificationForm.body}
                  onChange={e =>
                    setNotificationForm({
                      ...notificationForm,
                      body: e.target.value,
                    })
                  }
                  rows={4}
                  className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  placeholder="Notification message"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleBulkSendNotification}
                  disabled={
                    !notificationForm.title ||
                    !notificationForm.body ||
                    isBulkSendingNotification
                  }
                  className="flex-1 bg-black text-yellow-400 py-3 px-4 rounded-lg hover:bg-yellow-500 hover:text-black font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isBulkSendingNotification ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-400 mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} className="inline mr-2" />
                      Send to {selectedUsers.length} Users
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setBulkSendingNotification(false);
                    setNotificationForm({ title: '', body: '' });
                  }}
                  disabled={isBulkSendingNotification}
                  className="flex-1 bg-gray-300 text-black py-3 px-4 rounded-lg hover:bg-gray-400 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Send Email Modal */}
      {bulkSendingEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full border-2 border-black">
            <div className="p-6 border-b-2 border-black bg-black">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-yellow-400">
                  Send Email to {selectedUsers.length} Users
                </h2>
                <button
                  onClick={() => {
                    setBulkSendingEmail(false);
                    setEmailForm({ subject: '', message: '' });
                  }}
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={e =>
                    setEmailForm({ ...emailForm, subject: e.target.value })
                  }
                  className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  placeholder="Email subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={emailForm.message}
                  onChange={e =>
                    setEmailForm({ ...emailForm, message: e.target.value })
                  }
                  rows={6}
                  className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  placeholder="Email message"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleBulkSendEmail}
                  disabled={
                    !emailForm.subject ||
                    !emailForm.message ||
                    isBulkSendingEmail
                  }
                  className="flex-1 bg-black text-yellow-400 py-3 px-4 rounded-lg hover:bg-yellow-500 hover:text-black font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isBulkSendingEmail ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-400 mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} className="inline mr-2" />
                      Send to {selectedUsers.length} Users
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setBulkSendingEmail(false);
                    setEmailForm({ subject: '', message: '' });
                  }}
                  disabled={isBulkSendingEmail}
                  className="flex-1 bg-gray-300 text-black py-3 px-4 rounded-lg hover:bg-gray-400 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Send Message Modal */}
      {bulkSendingMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full border-2 border-black">
            <div className="p-6 border-b-2 border-black bg-black">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-yellow-400">
                  Send Message to {selectedUsers.length} Users
                </h2>
                <button
                  onClick={() => {
                    setBulkSendingMessage(false);
                    setMessageForm({ text: '' });
                  }}
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={messageForm.text}
                  onChange={e =>
                    setMessageForm({ ...messageForm, text: e.target.value })
                  }
                  rows={6}
                  className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                  placeholder="Type your message here..."
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleBulkSendMessage}
                  disabled={!messageForm.text.trim() || isBulkSendingMessage}
                  className="flex-1 bg-black text-yellow-400 py-3 px-4 rounded-lg hover:bg-yellow-500 hover:text-black font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isBulkSendingMessage ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-400 mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} className="inline mr-2" />
                      Send to {selectedUsers.length} Users
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setBulkSendingMessage(false);
                    setMessageForm({ text: '' });
                  }}
                  disabled={isBulkSendingMessage}
                  className="flex-1 bg-gray-300 text-black py-3 px-4 rounded-lg hover:bg-gray-400 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Admin Modal */}
      {showCreateAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 bg-black">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-yellow-400">
                  Create New Admin
                </h2>
                <button
                  onClick={() => {
                    setShowCreateAdmin(false);
                    setNewAdminForm({
                      email: '',
                      password: '',
                      confirmPassword: '',
                      firstName: '',
                      lastName: '',
                      username: '',
                      phoneNumber: '',
                    });
                  }}
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={newAdminForm.email}
                  onChange={e =>
                    setNewAdminForm({ ...newAdminForm, email: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="admin@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  value={newAdminForm.password}
                  onChange={e =>
                    setNewAdminForm({
                      ...newAdminForm,
                      password: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Minimum 6 characters"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  value={newAdminForm.confirmPassword}
                  onChange={e =>
                    setNewAdminForm({
                      ...newAdminForm,
                      confirmPassword: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Re-enter password"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={newAdminForm.firstName}
                    onChange={e =>
                      setNewAdminForm({
                        ...newAdminForm,
                        firstName: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="John"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={newAdminForm.lastName}
                    onChange={e =>
                      setNewAdminForm({
                        ...newAdminForm,
                        lastName: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username (Optional)
                </label>
                <input
                  type="text"
                  value={newAdminForm.username}
                  onChange={e =>
                    setNewAdminForm({
                      ...newAdminForm,
                      username: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="johndoe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  value={newAdminForm.phoneNumber}
                  onChange={e =>
                    setNewAdminForm({
                      ...newAdminForm,
                      phoneNumber: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="+1234567890"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateAdmin(false);
                  setNewAdminForm({
                    email: '',
                    password: '',
                    confirmPassword: '',
                    firstName: '',
                    lastName: '',
                    username: '',
                    phoneNumber: '',
                  });
                }}
                className="px-4 py-2 bg-gray-300 text-black rounded-lg hover:bg-gray-400 font-semibold"
                disabled={creatingAdmin}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAdmin}
                disabled={creatingAdmin}
                className="px-6 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 font-semibold flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creatingAdmin ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus size={18} className="mr-2" />
                    Create Admin
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
